class CartAttributes extends HTMLElement {
  constructor() {
    super();

    this.formId = this.dataset.form;
    this.form = document.getElementById(this.formId);

    // Find the checkout button belonging to this form.
    this.submit =
      Array.from(document.querySelectorAll('button[name="checkout"]')).find(
        (button) => button.form === this.form
      ) || document.querySelector('button[name="checkout"]');

    this.orderType = this.querySelector('#CartAttributesOrderType');
    this.serviceFields = this.querySelector('.service-awards-fields');
    this.corpFields = this.querySelector('.corporate-fields');
    this.error = this.querySelector('#CartAttributesError');

    if (
      !this.form ||
      !this.submit ||
      !this.orderType ||
      !this.serviceFields ||
      !this.corpFields
    ) {
      console.warn('Cart attributes could not initialize.', {
        form: this.form,
        submit: this.submit,
        orderType: this.orderType
      });

      return;
    }

    this.serviceInputs = this.serviceFields.querySelectorAll('input');
    this.corpInputs = this.corpFields.querySelectorAll('input');

    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);

    // Validate immediately while typing.
    this.addEventListener('input', this.handleInput);

    // Save attributes when a field is committed or the select changes.
    this.addEventListener('change', this.handleChange);

    // Prevent submission as a second layer of protection.
    this.form.addEventListener('submit', this.handleSubmit, true);
    this.submit.addEventListener(
      'click',
      this.handleCheckoutClick,
      true
    );

    // Recheck when Firefox restores the page from history/cache.
    window.addEventListener('pageshow', this.handlePageShow);

    this.updateFieldVisibility();
    this.validateForm();
  }

  handleInput() {
    this.validateForm();
  }

  handleChange(event) {
    if (event.target === this.orderType) {
      this.updateFieldVisibility();
    }

    // Do not wait for the cart AJAX request before disabling/enabling checkout.
    this.validateForm();

    const cartAttributes = this.getAttributes();
    this.updateCart(cartAttributes);
  }

  handleSubmit(event) {
    if (!this.validateForm()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.form.reportValidity();
    }
  }

  handleCheckoutClick(event) {
    if (!this.validateForm()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.form.reportValidity();
    }
  }

  handlePageShow() {
    this.updateFieldVisibility();
    this.validateForm();
  }

  getAttributes() {
    const formData = new FormData(this.form);
    const attributes = {};

    formData.forEach((value, key) => {
      if (key.startsWith('attributes[')) {
        const cleanKey = key.replace(/^attributes\[|\]$/g, '');
        attributes[cleanKey] = value;
      }
    });

    // Clear attributes that no longer apply when the order type changes.
    if (this.orderType.value !== 'Service Awards') {
      attributes['Employee Name'] = '';
      attributes['Year of Service'] = '';
    }

    if (this.orderType.value !== 'Corporate Orders') {
      attributes['Department Number'] = '';
      attributes['Project Code'] = '';
      attributes['GL Code'] = '';
    }

    return attributes;
  }

  async updateCart(attributes) {
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        throw new Error(`Cart update failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to save cart attributes:', error);
    } finally {
      this.validateForm();
    }
  }

  updateFieldVisibility() {
    const isServiceAwards =
      this.orderType.value === 'Service Awards';

    const isCorporate =
      this.orderType.value === 'Corporate Orders';

    this.serviceFields.style.display = isServiceAwards
      ? 'block'
      : 'none';

    this.corpFields.style.display = isCorporate
      ? 'block'
      : 'none';

    // Disabled inputs are excluded from native form validation.
    this.serviceInputs.forEach((input) => {
      input.disabled = !isServiceAwards;
    });

    this.corpInputs.forEach((input) => {
      input.disabled = !isCorporate;
    });
  }

  validateForm() {
    if (!this.form || !this.submit) {
      return false;
    }

    const isValid = this.form.checkValidity();

    // This is what actually disables the checkout button.
    this.submit.disabled = !isValid;

    // Retain your theme's visual disabled styling.
    this.submit.classList.toggle('disabled', !isValid);

    this.submit.setAttribute(
      'aria-disabled',
      String(!isValid)
    );

    if (this.error) {
      this.error.classList.toggle('hidden', isValid);
    }

    return isValid;
  }
}

if (!customElements.get('cart-attributes')) {
  customElements.define('cart-attributes', CartAttributes);
}